import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import { Radio } from 'common-src/components/base';
import { getActivePatientCaregivers } from 'common-src/models/PatientCaregiver/selectors';
import { getFormattedPhoneNumber } from 'common-src/models/PatientPhoneNumber/helpers';

import { InfoItem, SeparatorLine } from 'src/components/elements';
import useCustomSelector from 'src/hooks/useCustomSelector';
import commonStyles from 'src/pages/DocumentationIndex/commonStyles.module.scss';
import { ModuleTitle } from 'src/pages/DocumentationIndex/components';

import styles from './CaregiverInfoModule.module.scss';

const CaregiverInfoModule = ({ patientId, caregiverInfo, setCaregiverInfo }) => {
  const caregivers = useCustomSelector((state) => getActivePatientCaregivers(state, patientId));

  useEffect(() => {
    if (_.isEmpty(caregivers)) return;

    const primaryCaregiver = caregivers?.find((cg) => cg.isPrimary);
    const secondaryCaregivers = caregivers?.filter((cg) => !cg.isPrimary) ?? [];
    const hasPrimaryCaregiver = !_.isEmpty(primaryCaregiver);

    setCaregiverInfo((prev) => ({
      ...prev,
      hasPrimaryCaregiver,
      primaryCaregiver: hasPrimaryCaregiver
        ? {
            name: primaryCaregiver.getName(),
            phone: getFormattedPhoneNumber(
              primaryCaregiver.phone.startsWith('+1')
                ? primaryCaregiver.phone.substring(2)
                : primaryCaregiver.phone,
            ),
            phoneType: primaryCaregiver.phoneType,
            email: primaryCaregiver.email,
            relationship: primaryCaregiver.relationship,
            livesWithPatient: primaryCaregiver.livesWithPatient,
          }
        : null,
      secondaryCaregivers: secondaryCaregivers.map((cg) => ({
        name: cg.getName(),
        relationship: cg.relationship,
        phone: getFormattedPhoneNumber(
          cg.phone.startsWith('+1') ? cg.phone.substring(2) : cg.phone,
        ),
      })),
    }));
  }, [caregivers]);

  const renderContent = () => {
    if (_.isEmpty(caregivers)) return <p className={styles.placeholder}>No caregivers available</p>;

    return (
      <>
        <p className={['font-s-14', 'font-w-600', 'm-b-14'].join(' ')}>
          Primary caregiver: (relative or unpaid CG that assists with ADLs and/or IADLs)
        </p>
        <div
          className={['flex-row', 'vertically-centered', 'gap-14', 'm-b-20', 'm-t-20'].join(' ')}
        >
          <Radio
            classNames={[commonStyles.radio]}
            checked={caregiverInfo.hasPrimaryCaregiver}
            label="Has a primary caregiver"
            name="cg-radio"
            disabled
            id="has-primary-caregiver"
          />
          <Radio
            classNames={[commonStyles.radio]}
            checked={!caregiverInfo.hasPrimaryCaregiver}
            label="No caregiver"
            name="cg-radio"
            disabled
            id="no-caregiver"
          />
        </div>
        {caregiverInfo.hasPrimaryCaregiver && (
          <div className={[styles.grid, 'm-b-20'].join(' ')}>
            <InfoItem
              textId="primary-caregiver-name"
              title="Name"
              content={caregiverInfo.primaryCaregiver.name}
              isSmall
            />
            <InfoItem
              textId="primary-caregiver-phone"
              title="Phone"
              content={caregiverInfo.primaryCaregiver.phone}
              isSmall
            />
            <InfoItem
              textId="primary-caregiver-phone-type"
              title="Phone Type"
              content={caregiverInfo.primaryCaregiver.phoneType}
              isSmall
            />
            <InfoItem
              textId="primary-caregiver-email"
              title="Email"
              content={caregiverInfo.primaryCaregiver.email}
              isSmall
            />
            <InfoItem
              textId="primary-caregiver-relationship"
              title="Relationship"
              content={caregiverInfo.primaryCaregiver.relationship}
              isSmall
            />
            <InfoItem
              textId="primary-caregiver-lives-with-patient"
              title="Lives with patient"
              content={caregiverInfo.primaryCaregiver.livesWithPatient ? 'Yes' : 'No'}
              isSmall
            />
          </div>
        )}
        {caregiverInfo.secondaryCaregivers?.length > 0 && (
          <>
            <p className={['font-s-14', 'font-w-600', 'm-b-14'].join(' ')}>Secondary Caregivers:</p>
            <>
              {caregiverInfo.secondaryCaregivers.map((cg, index) => (
                <div key={cg.name}>
                  <div className={[styles.grid, 'm-t-20', 'm-b-20'].join(' ')}>
                    <InfoItem
                      textId={`secondary-caregiver-name-${index}`}
                      title="Name"
                      content={cg.name}
                      isSmall
                    />
                    <InfoItem
                      textId={`secondary-caregiver-relationship-${index}`}
                      title="Relationship"
                      content={cg.relationship}
                      isSmall
                    />
                    <InfoItem
                      textId={`secondary-caregiver-phone-${index}`}
                      title="Phone"
                      content={cg.phone}
                      isSmall
                    />
                  </div>
                  <SeparatorLine classNames={['m-t-20']} />
                </div>
              ))}
            </>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <ModuleTitle title="Caregiver Info" />
      <div className={styles.container}>{renderContent()}</div>
    </>
  );
};

CaregiverInfoModule.propTypes = {
  patientId: PropTypes.number,
  caregiverInfo: PropTypes.object,
  setCaregiverInfo: PropTypes.func,
};

export default CaregiverInfoModule;
